import React, { useState } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { styled } from '@mui/material/styles'
import { Theme } from '@mui/material/styles'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import { MIKE_COLORS } from '@mike/mike-shared-frontend/mike-shared-styles/mike-colors'
import withFormik from './withFormik'

type MikeTheme = Theme & {
  palette: Theme['palette'] & {
    mediumGrey?: { main: string }
  }
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  height: 'auto',
  minWidth: theme.spacing(15)
}))

const StyledProgress = styled(LinearProgress)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(6),
  left: 0,
  width: '100%'
}))

const StyledHelperTextPlaceholder = styled('div')(({ theme }) => ({
  minHeight: theme.spacing(2),
  backgroundColor: 'transparent'
}))

const SelectField = ({
  autoFocus,
  disabled,
  error,
  fullWidth,
  helperText,
  hideIcon,
  items,
  label,
  loading,
  name,
  onBlur,
  onChange,
  onSelectClose,
  onSelectOpen,
  primaryField,
  required,
  secondaryField,
  selectOpen,
  value,
  valueField,
  formik
}) => {
  const [open, setOpen] = useState(false)

  const isOpen = selectOpen || open

  const handleOpen = (...args) => {
    onSelectOpen && onSelectOpen(...args)
    setOpen(true)
  }

  const handleClose = (...args) => {
    onSelectClose && onSelectClose(...args)
    setOpen(false)
  }

  const handleChange = (...args) => {
    setOpen(false)
    onChange(...args)
  }

  const forceUpdateOnItemListChange = `${name}-${items.length}`

  return (
    <StyledFormControl
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      error={error}
      margin={'dense'}
      variant={label ? 'filled' : 'standard'}
    >
      {label && (
        <InputLabel htmlFor={name} required={false} disabled={disabled}>
          {label}
        </InputLabel>
      )}

      <Select
        key={forceUpdateOnItemListChange}
        autoFocus={autoFocus}
        inputProps={{
          name,
          id: name
        }}
        sx={{
          '& .MuiSelect-select':
            value !== ''
              ? label
                ? (theme: Theme) => ({
                    paddingTop: theme.spacing(2.25),
                    paddingBottom: 0
                  })
                : (theme: Theme) => ({
                    paddingTop: theme.spacing(1.5),
                    paddingBottom: theme.spacing(0.75)
                  })
              : {},
          '& .MuiSelect-icon': hideIcon
            ? { opacity: 0 }
            : error
            ? (theme: Theme) => ({ fill: theme.palette.error.main })
            : disabled
            ? (theme: Theme) => {
                const t = theme as MikeTheme
                return { fill: t.palette.mediumGrey?.main ?? '#999' }
              }
            : {}
        }}
        MenuProps={{
          PaperProps: {
            sx: { backgroundColor: MIKE_COLORS.MEDIUMGREY_LIGHT }
          }
        }}
        onBlur={onBlur}
        onChange={handleChange}
        onClose={handleClose}
        onOpen={handleOpen}
        open={isOpen}
        value={value}
      >
        {items.map(item => {
          const itemValue = get(item, valueField)
          const selectedItemValue = get(formik.values, name)
          const primary = get(item, primaryField)
          const secondary = get(item, secondaryField)

          const isPrimaryDisabled = disabled
          const isPrimarySelected = itemValue === selectedItemValue && isOpen

          return (
            <MenuItem key={itemValue} value={itemValue}>
              <ListItemText
                sx={{
                  '& .MuiListItemText-primary': isPrimaryDisabled
                    ? (theme: Theme) => {
                        const t = theme as MikeTheme
                        return { color: t.palette.mediumGrey?.main ?? '#999' }
                      }
                    : isPrimarySelected
                    ? (theme: Theme) => ({
                        color: theme.palette.primary.main,
                        fontWeight: theme.typography.fontWeightBold
                      })
                    : {}
                }}
                primary={primary}
                secondary={
                  itemValue === selectedItemValue && !isOpen ? null : secondary
                }
              />
            </MenuItem>
          )
        })}
      </Select>

      {loading && <StyledProgress />}

      {helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : (
        <StyledHelperTextPlaceholder />
      )}
    </StyledFormControl>
  )
}
SelectField.propTypes = {
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  formik: PropTypes.object.isRequired,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  hideIcon: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  label: PropTypes.string,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onSelectClose: PropTypes.func,
  onSelectOpen: PropTypes.func,
  primaryField: PropTypes.string.isRequired,
  required: PropTypes.bool,
  secondaryField: PropTypes.string,
  selectOpen: PropTypes.bool,
  value: PropTypes.any.isRequired,
  valueField: PropTypes.string.isRequired
}

export default withFormik(SelectField)
